import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Box, Typography, Chip, Paper } from '@mui/material';
import { LeadCard } from './LeadCard.js';
import type { Lead, PipelineEtapa } from '@azoupvendas/shared';

const ETAPA_CONFIG: Record<PipelineEtapa, { label: string; color: string; bgColor: string }> = {
  novo: { label: 'NOVO', color: '#1976d2', bgColor: '#e3f2fd' },
  qualificado: { label: 'QUALIFICADO', color: '#ed6c02', bgColor: '#fff3e0' },
  proposta: { label: 'PROPOSTA', color: '#9c27b0', bgColor: '#f3e5f5' },
  ganho: { label: 'GANHO', color: '#2e7d32', bgColor: '#e8f5e9' },
  perdido: { label: 'PERDIDO', color: '#d32f2f', bgColor: '#ffebee' }
};

interface KanbanColumnProps {
  etapa: PipelineEtapa;
  leads: Lead[];
  onCardClick: (lead: Lead) => void;
}

// AIDEV-NOTE: Coluna droppable do Kanban — usa useDroppable do @dnd-kit/core e SortableContext para os cards
export function KanbanColumn({ etapa, leads, onCardClick }: KanbanColumnProps) {
  const { setNodeRef, isOver } = useDroppable({ id: etapa });
  const config = ETAPA_CONFIG[etapa];
  // AIDEV-NOTE: Total em R$ dos leads na coluna — exibido no header abaixo do count chip
  const totalValor = leads.reduce((sum, l) => sum + (l.valor_estimado ?? 0), 0);

  return (
    <Box
      sx={{
        minWidth: 200,
        maxWidth: 220,
        flexShrink: 0,
        bgcolor: isOver ? 'action.hover' : 'grey.100',
        borderRadius: 2,
        p: 1.5,
        transition: 'background-color 0.2s'
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="caption" fontWeight={700} sx={{ color: config.color }}>
          {config.label}
        </Typography>
        <Chip
          label={leads.length}
          size="small"
          sx={{
            bgcolor: config.bgColor,
            color: config.color,
            fontWeight: 700,
            height: 20,
            fontSize: '10px'
          }}
        />
      </Box>
      <Typography variant="caption" color="text.secondary" mb={0.5} display="block">
        {totalValor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
      </Typography>

      <Box ref={setNodeRef} minHeight={60}>
        <SortableContext items={leads.map((l) => l.id)} strategy={verticalListSortingStrategy}>
          {leads.map((lead) => (
            <LeadCard key={lead.id} lead={lead} onClick={onCardClick} />
          ))}
        </SortableContext>

        {leads.length === 0 && (
          <Paper
            variant="outlined"
            sx={{ p: 1, textAlign: 'center', borderStyle: 'dashed', borderColor: 'grey.300' }}
          >
            <Typography variant="caption" color="text.disabled">
              Solte aqui
            </Typography>
          </Paper>
        )}
      </Box>
    </Box>
  );
}
